import { Suspense, useMemo } from "react"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useUser } from "@/apis/bff"
import { Banner } from "@/components/ui/banner"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { FormError } from "@/components/ui/form"
import { TextArea } from "@/components/ui/input"
import { CustomToggleGroup, ToggleItem } from "@/components/ui/toggle-group"
import { cn } from "@/lib/utils"
import { CareProviderSearch } from "@/modules/care-provider/simple-search/care-provider-search"
import { AgendaAction } from "@/modules/patients/agenda"
import { AlternativeTriageFieldset } from "@/modules/patients/agenda/time-window/components/alternative-triage-fieldset"
import { ReasonSelect } from "@/modules/patients/agenda/time-window/components/reason-select"
import { ReferralToFieldset } from "@/modules/patients/agenda/time-window/components/referral-to-fieldset"
import { TimeWindowDateRange } from "@/modules/patients/agenda/time-window/components/time-window-date-range"
import { TimeWindowErrorCorrection } from "@/modules/patients/agenda/time-window/components/time-window-error-correction"
import { useTimeWindowDateActions } from "@/modules/patients/agenda/time-window/hooks/useTimeWindowDateRange"
import { calculateDateRange } from "@/modules/patients/agenda/time-window/lib/calculate-date-range"
import {
  checkReasonIfPost,
  TimeWindowFormSchema,
  timeWindowTextMaxLength,
} from "@/modules/patients/agenda/time-window/time-window-schema"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { useSwissReferredByReasonsQuery } from "@/modules/patients/services/ch/useReferredByReasonsQuery"
import { ReferralReason, ReferredBy } from "@/types/__generated__/union-types"

interface TimeWindowFormProps {
  patient: PatientFragment$data
  mode: AgendaAction
  timeWindow?: TimeWindowFormSchema
  progressNoteId?: string | null
  reasonTriggerRef: React.RefObject<HTMLButtonElement | null>
}

const TimeWindowForm = ({ patient, mode, timeWindow, progressNoteId, reasonTriggerRef }: TimeWindowFormProps) => {
  const { t } = useTranslation()

  const { setValue, control, clearErrors, resetField, getValues, trigger } = useFormContext<TimeWindowFormSchema>()

  const watchedReferredBy = useWatch({ control, name: "referredBy" })
  const watchedReferralReason = useWatch({ control, name: "referralReason" })

  const { updateDateRange } = useTimeWindowDateActions()

  const { role } = useUser()
  const isCSC = role === "CustomerServiceCenter"
  const isViewMode = mode === "VIEW"
  const isEditMode = mode === "EDIT"
  const isPostReason = checkReasonIfPost(watchedReferralReason)
  const isCareProviderReferral = watchedReferredBy === "CARE_PROVIDER"

  const referredByReasons = useSwissReferredByReasonsQuery()
  const referralReasonsOptions = useMemo(() => {
    return referredByReasons.ch_referredByReasons.filter((x) => x.key === watchedReferredBy).flatMap((x) => x.value)
  }, [referredByReasons, watchedReferredBy])

  const referrerItems: ToggleItem[] = useMemo(
    () => [
      { value: "MEDGATE", text: t("patient.referredBy.MEDGATE") },
      { value: "SELF", text: t("patient.referredBy.SELF") },
      { value: "CARE_PROVIDER", text: t("patient.referredBy.CARE_PROVIDER") },
    ],
    [t],
  )

  const onReasonSelect = (val: ReferralReason) => {
    setValue("referralReason", val, { shouldValidate: true })
    trigger("referralToCareProviderId")

    const datePreset = calculateDateRange(val, new Date())
    updateDateRange(datePreset.startDate, datePreset.endDate)
  }

  const resetAlternativeTriageFields = () => {
    resetField("alternativeTriagePhoneNumber")
    resetField("alternativeTriageReason")
  }

  const handleChangeReferredBy = (value: ReferredBy) => {
    if (value) {
      clearErrors(["alternativeTriagePhoneNumber", "alternativeTriageReason", "referredByExternalCareProviderId"])
      setValue("referredBy", value, { shouldValidate: true })
      resetAlternativeTriageFields()

      // Keep the reason valid for the newly selected Referred by: if the current reason is no longer
      // offered, fall back to the first available option.
      const reasonsForReferredBy = referredByReasons.ch_referredByReasons
        .filter((reason) => reason.key === value)
        .flatMap((reason) => reason.value)
      if (reasonsForReferredBy.length > 0 && !reasonsForReferredBy.includes(watchedReferralReason)) {
        setValue("referralReason", reasonsForReferredBy[0], { shouldValidate: true })
        trigger("referralToCareProviderId")
      }

      if (value === "MEDGATE" && getValues("dateFrom") < new Date()) {
        const datePreset = calculateDateRange("APPOINTMENT_REGISTRATION", new Date())
        updateDateRange(datePreset.startDate, datePreset.endDate)
      }
    }
  }

  return (
    <div className="animate-fade-in-top grid content-start gap-5 p-3">
      {role === "CustomerServiceCenter" && <TimeWindowErrorCorrection isViewMode={isViewMode} />}
      <fieldset>
        <legend className="text-on-surface mb-3 text-base font-medium" data-testid="referred-by-legend">
          {t("patient.agenda.timeWindowForm.referredBy")}
        </legend>
        <Controller
          name="referredBy"
          control={control}
          render={({ field: { value }, fieldState: { error } }) => (
            <>
              <CustomToggleGroup
                testId="referredBy-toggle-group"
                className="col-span-1"
                items={referrerItems}
                value={value ?? ""}
                invalid={!!error}
                disabled={isViewMode || !!progressNoteId}
                onValueChange={handleChangeReferredBy}
              />
              {error?.message && <FormError>{t(error.message)}</FormError>}
            </>
          )}
        />
      </fieldset>
      <div hidden={!isCareProviderReferral} data-testid="referred-by-care-provider">
        <Controller
          name="referredByExternalCareProviderId"
          control={control}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <CareProviderSearch
              label={`${t("patient.agenda.timeWindowForm.referredByExternalCareProviderId")}*`}
              searchType="TIME_WINDOW_WITH_REFERRED_BY"
              name={name}
              value={value}
              analyticsSectionName="timewindow_referredby"
              disabled={isViewMode}
              onSelect={onChange}
              onRemove={() => onChange("")}
              error={t(error?.message || "")}
            />
          )}
        />
      </div>
      <fieldset className="grid gap-5">
        <legend className="text-on-surface mb-3 text-base font-medium" data-testid="time-window-legend">
          {t("patient.agenda.timeWindow")}
        </legend>

        <div
          className={cn(
            "grid gap-5 transition-[filter,opacity]",
            !watchedReferredBy && "pointer-events-none opacity-90 blur-[2px]",
          )}>
          <Controller
            name="referralReason"
            defaultValue="APPOINTMENT_REGISTRATION"
            control={control}
            render={({ field: { value }, fieldState: { error } }) => (
              <Suspense fallback={null}>
                <ReasonSelect
                  referralReasonsOptions={referralReasonsOptions}
                  testId="reason-select"
                  disabled={isViewMode || !!progressNoteId || !watchedReferredBy}
                  label={`${t("patient.agenda.timeWindowForm.reason")}*`}
                  error={t(error?.message ?? "")}
                  onSelect={onReasonSelect}
                  value={value}
                  triggerRef={reasonTriggerRef}
                />
              </Suspense>
            )}
          />
          {watchedReferredBy === "MEDGATE" && (
            <Controller
              name="alternativeTriage"
              defaultValue={!!timeWindow?.alternativeTriage}
              control={control}
              render={({ field: { value, onChange } }) => (
                <DetailCheckbox
                  disabled={isViewMode || isEditMode}
                  className="w-fit"
                  data-testid="alternative-triage-checkbox"
                  checked={value}
                  onCheckedChange={(checked) => {
                    onChange(checked)
                    if (!checked) {
                      resetAlternativeTriageFields()
                    } else if (patient.phoneNumber) {
                      setValue("alternativeTriagePhoneNumber", patient.phoneNumber)
                    }
                  }}>
                  {t("patient.agenda.timeWindowForm.alternativeTriage")}
                </DetailCheckbox>
              )}
            />
          )}
          <TimeWindowDateRange disabled={isViewMode || !watchedReferredBy} isPostReason={isPostReason} isCSC={isCSC} />
          <Controller
            name="internalComment"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextArea
                {...field}
                data-testid="text-area"
                label={t("patient.agenda.timeWindowForm.internalComment")}
                error={t(error?.message || "")}
                disabled={isViewMode || !watchedReferredBy}
                textLimit={timeWindowTextMaxLength}
              />
            )}
          />
        </div>
        {!watchedReferredBy && (
          <Banner type="inline" variant="warning" data-testid="referred-by-required-warning">
            {t("patient.agenda.timeWindowForm.referredByRequiredWarning")}
          </Banner>
        )}
      </fieldset>
      <AlternativeTriageFieldset isViewMode={isViewMode} isEditMode={isEditMode} />
      <ReferralToFieldset
        isViewMode={isViewMode}
        isEligibleForPreventionProgram={patient.isEligibleForPreventionProgram}
      />
    </div>
  )
}

export { TimeWindowForm }
